import { yupResolver } from '@hookform/resolvers/yup';
import {
	Box,
	Button,
	Card,
	CircularProgress,
	Collapse,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	Stack,
	Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import CashBalanceApi from '../../../api/CashBalanceApi';
import PolicyApi from '../../../api/PolicyApi';
import { getUsePoliciesForPolicyholderQueryKey } from '../../../api/PolicyholderApi';
import { useAuthContext } from '../../../context/auth';
import { useConfigContext } from '../../../context/config';
import { CashBalanceTransferRequest } from '../../../types/cashBalance';
import { CurrencyCode } from '../../../types/currencyCode';
import {
	CurrencyField,
	PolicyholderLookupField,
	PolicyLookupField,
	SelectField,
	StringField,
} from '../../fastForm/fields';
import { errorToast, successToast } from '../../Toast';
import {
	BALANCE_TRANSFER_CONFIRM__CANCEL_BUTTON,
	BALANCE_TRANSFER_CONFIRM__HEADER,
	BALANCE_TRANSFER_CONFIRM__PROCEED_BUTTON,
	BALANCE_TRANSFER_CONFIRM__SUBHEADER,
	BALANCE_TRANSFER_MODAL__AMOUNT_HELPER,
	BALANCE_TRANSFER_MODAL__AMOUNT_TITLE,
	BALANCE_TRANSFER_MODAL__CANCEL_BUTTON,
	BALANCE_TRANSFER_MODAL__COMMENTS_TITLE,
	BALANCE_TRANSFER_MODAL__HEADER,
	BALANCE_TRANSFER_MODAL__POLICYHOLDER_TITLE,
	BALANCE_TRANSFER_MODAL__POLICY_TITLE,
	BALANCE_TRANSFER_MODAL__TRANSFER_BUTTON,
	BALANCE_TRANSFER_MODAL__TRANSFER_FROM,
	BALANCE_TRANSFER_MODAL__TRANSFER_TO,
	BALANCE_TRANSFER_MODAL__TRANSFER_TYPE_TITLE,
	BALANCE_TRANSFER__ERROR_TOAST,
	BALANCE_TRANSFER__SUCCESS_TOAST,
} from '../data/constants';
import { balanceTransferSchema } from '../lib/balanceTransferSchema';

interface PolicyResult {
	product: string;
	locator: string;
	status:
		| 'Lapsed'
		| 'In Grace Period'
		| 'Reinstated'
		| 'Withdrawn'
		| 'Cancelled'
		| 'Issued'
		| 'Accepted'
		| 'Application In Progress';
	isIssued: boolean;
}

interface FormValues {
	balanceTransfer: {
		policyholderFrom: string;
		policyFrom: string;
		policyholderTo: string;
		policyTo: string;
		name: string;
		amount: string;
		details: string;
	};
}

interface TransferModalProps {
	open: boolean;
	cashBalanceAmount: string;
	cashBalanceCurrency: CurrencyCode;
	cashBalanceLocator: string;
	policyFrom: string;
	handleClose: () => void;
}

const BalanceTransferModal = (props: TransferModalProps) => {
	const {
		open,
		cashBalanceAmount,
		cashBalanceCurrency,
		cashBalanceLocator,
		policyFrom,
		handleClose,
	} = props;
	const queryClient = useQueryClient();
	const config = useConfigContext();
	const authData = useAuthContext();
	const { authorizationToken: authToken } = authData.auth;
	const transferConfigs =
		config?.cashManagementConfig?.configuration?.cashTransfers;
	const transferOptions = transferConfigs?.map((t) => t.displayName);
	const { policyholderId } = useParams();
	const [requiresDetail, setRequiresDetail] = React.useState(false);
	const [loading, setLoading] = React.useState(false);
	const [formWizard, setFormWizard] = useState<'confirm' | 'initial'>(
		parseFloat(cashBalanceAmount) > 0 ? 'initial' : 'confirm',
	);
	const defaultAmount =
		parseFloat(cashBalanceAmount) > 0
			? Math.abs(parseFloat(cashBalanceAmount)).toFixed(
					cashBalanceAmount.split('.')[1]?.length,
			  )
			: parseFloat('0').toFixed(cashBalanceAmount.split('.')[1]?.length);
	const methods = useForm<FormValues>({
		defaultValues: {
			balanceTransfer: {
				policyholderFrom: policyholderId || '',
				policyFrom: policyFrom || '',
				policyholderTo: policyholderId || '',
				policyTo: '',
				name: '',
				amount: defaultAmount || '',
				details: '',
			},
		},
		resolver: yupResolver(balanceTransferSchema(requiresDetail)),
	});

	const { control, watch, handleSubmit } = methods;

	const transferName = watch(`balanceTransfer.name`);
	const transferAmount = watch(`balanceTransfer.amount`);

	const transferType = transferConfigs.find(
		(c) => c.displayName === transferName,
	);
	const resultingCashBalance =
		parseFloat(cashBalanceAmount) - parseFloat(transferAmount);

	useEffect(() => {
		setRequiresDetail(transferType?.requireDetails);
	}, [transferType]);

	const onSubmit: SubmitHandler<FormValues> = async (formValues) => {
		setLoading(true);
		try {
			// fetch policy to get cash balance locator
			const policyData = await PolicyApi.getPolicy(
				authToken,
				formValues.balanceTransfer.policyTo,
			);
			const transferRequest: CashBalanceTransferRequest = {
				name: transferType.name,
				targetCashBalanceLocator: policyData.data?.cashBalance?.locator,
				amount: parseFloat(formValues.balanceTransfer.amount),
				details: formValues.balanceTransfer.details,
			};
			// make transfer
			await CashBalanceApi.transferCashBalance(
				authToken,
				cashBalanceLocator,
				transferRequest,
			);
			// invalidate policies query to refresh table
			queryClient.invalidateQueries(
				getUsePoliciesForPolicyholderQueryKey(
					formValues.balanceTransfer.policyholderFrom,
					authToken,
				),
			);
			successToast(BALANCE_TRANSFER__SUCCESS_TOAST);
		} catch (e) {
			errorToast(BALANCE_TRANSFER__ERROR_TOAST + e?.response?.data?.message);
		} finally {
			setLoading(false);
			handleClose();
		}
	};
	const getDialogContent = () => {
		return (
			<>
				<Collapse in={formWizard === 'confirm'}>
					<DialogTitle>{BALANCE_TRANSFER_CONFIRM__HEADER}</DialogTitle>
					<DialogContent>
						{BALANCE_TRANSFER_CONFIRM__SUBHEADER(
							parseFloat(cashBalanceAmount) < 0,
						)}
					</DialogContent>
					<DialogActions>
						<Button
							onClick={() => setFormWizard('initial')}
							autoFocus
							color='primary'
							variant='contained'
						>
							{BALANCE_TRANSFER_CONFIRM__PROCEED_BUTTON}
						</Button>
						<Button onClick={handleClose} color='neutralButton'>
							{BALANCE_TRANSFER_CONFIRM__CANCEL_BUTTON}
						</Button>
					</DialogActions>
				</Collapse>
				<Collapse in={formWizard === 'initial'}>
					<FormProvider {...methods}>
						<Box component='form' noValidate onSubmit={handleSubmit(onSubmit)}>
							<DialogTitle>{BALANCE_TRANSFER_MODAL__HEADER}</DialogTitle>
							<DialogContent>
								<Stack spacing={2}>
									<Card sx={{ paddingTop: '0' }}>
										<Typography
											variant='subtitle1'
											sx={{ marginTop: '5px', marginBottom: '5px' }}
										>
											{BALANCE_TRANSFER_MODAL__TRANSFER_FROM}
										</Typography>
										<Grid container spacing={2}>
											<Grid item xs={6}>
												<PolicyholderLookupField<FormValues>
													policyholderKey={`balanceTransfer.policyholderFrom`}
													order={1}
													name='balanceTransfer.policyholderFrom'
													title={BALANCE_TRANSFER_MODAL__POLICYHOLDER_TITLE}
													control={control}
													disabled
													type='text'
													values={[]}
												/>
											</Grid>
											<Grid item xs={6}>
												<PolicyLookupField<FormValues>
													policyholderKey={`balanceTransfer.policyholderFrom`}
													policyKey={`balanceTransfer.policyFrom`}
													order={2}
													name='balanceTransfer.policyFrom'
													title={BALANCE_TRANSFER_MODAL__POLICY_TITLE}
													control={control}
													disabled
													type='text'
													values={[]}
												/>
											</Grid>
										</Grid>
									</Card>
									<Card sx={{ paddingTop: '0' }}>
										<Typography
											variant='subtitle1'
											sx={{ marginTop: '5px', marginBottom: '5px' }}
										>
											{BALANCE_TRANSFER_MODAL__TRANSFER_TO}
										</Typography>

										<Grid container spacing={2}>
											<Grid item xs={6}>
												<PolicyholderLookupField<FormValues>
													policyholderKey={`balanceTransfer.policyholderTo`}
													order={3}
													name='balanceTransfer.policyholderTo'
													title={BALANCE_TRANSFER_MODAL__POLICYHOLDER_TITLE}
													control={control}
													disabled={loading}
													type='text'
													values={[]}
												/>
											</Grid>
											<Grid item xs={6}>
												<PolicyLookupField<FormValues>
													policyholderKey={`balanceTransfer.policyholderTo`}
													policyKey={`balanceTransfer.policyTo`}
													order={4}
													name='balanceTransfer.policyTo'
													title={BALANCE_TRANSFER_MODAL__POLICY_TITLE}
													control={control}
													disabled={loading}
													type='text'
													values={[]}
													policiesFilter={(p: PolicyResult) =>
														p.isIssued && p.locator !== policyFrom
													} // only allow issued policies to select
												/>
											</Grid>
										</Grid>
									</Card>
									<SelectField<FormValues>
										order={5}
										name={`balanceTransfer.name`}
										title={BALANCE_TRANSFER_MODAL__TRANSFER_TYPE_TITLE}
										disabled={loading}
										control={control}
										type='select'
										values={transferOptions}
									/>
									<CurrencyField
										order={6}
										name={`balanceTransfer.amount`}
										title={BALANCE_TRANSFER_MODAL__AMOUNT_TITLE}
										currency={cashBalanceCurrency}
										disabled={loading}
										control={control}
										helperText={
											resultingCashBalance < 0 &&
											BALANCE_TRANSFER_MODAL__AMOUNT_HELPER(policyFrom)
										}
										type='select'
									/>
									<StringField<FormValues>
										order={7}
										name={`balanceTransfer.details`}
										title={BALANCE_TRANSFER_MODAL__COMMENTS_TITLE}
										multiline
										optional={
											transferType ? !transferType.requireDetails : true
										}
										disabled={loading}
										control={control}
										type='string'
										values={[]}
									/>
								</Stack>
							</DialogContent>
							<DialogActions>
								<Button onClick={handleClose} color='neutralButton'>
									{BALANCE_TRANSFER_MODAL__CANCEL_BUTTON}
								</Button>
								<Button
									type='submit'
									variant='contained'
									color='secondary'
									disabled={loading}
									startIcon={loading && <CircularProgress size={20} />}
								>
									{BALANCE_TRANSFER_MODAL__TRANSFER_BUTTON}
								</Button>
							</DialogActions>
						</Box>
					</FormProvider>
				</Collapse>
			</>
		);
	};

	return (
		<Dialog open={open} onClose={handleClose} scroll='paper'>
			<Box
				sx={{
					width: {
						xs: '90%',
						sm: '90%',
						md: 600,
						lg: 600,
						xl: 600,
					},
					overflowX: 'hidden',
				}}
			>
				{getDialogContent()}
			</Box>
		</Dialog>
	);
};

export default BalanceTransferModal;
